import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import DocStatusTag from '_components/utils/DocStatusTag';
import PdfDocument from '../PdfDocument';
import {
  DateInput,
  EMPTY_SECTIONS,
  FieldError,
  RequiredMark,
  SignatureField,
  SupervisorSections,
  firstErrors,
  refusalNoteError,
} from '../FormControls';
import { getFieldErrors, getLayoffStatus } from '../useLayoff';
import {
  useRecordRefusalAsSigner,
  useSavedSignature,
  useSignatureDetail,
  useSignaturePreview,
  useSignLayoff,
} from './useSignatures';
import styles from '../layoff.module.scss';

const formatDate = (value) =>
  value ? moment(value).format('MMM D, YYYY') : '';

/**
 * Client-side checks that mirror SignLayoffRequest, so most mistakes show before submitting
 */
const validate = ({ effectiveDate, form, signature, confirm }, minDate) => {
  const errors = {};
  if (!effectiveDate) errors.effective_date = 'Choose the last day worked.';
  else if (effectiveDate < minDate)
    errors.effective_date = "The last day worked can't be in the past.";
  if (!form.reason.trim())
    errors['form.reason'] = 'Enter the reason for separation.';
  if (!form.timesheets)
    errors['form.timesheets'] = 'Choose whether all timesheets are turned in.';
  if (!form.rehire_eligible)
    errors['form.rehire_eligible'] =
      'Choose whether the employee is eligible for rehire.';
  if (form.rehire_eligible === 'yes' && !form.rehire_date)
    errors['form.rehire_date'] = 'Enter the estimated rehire date.';
  if (form.rehire_eligible === 'conditional' && !form.rehire_conditions.trim())
    errors['form.rehire_conditions'] = 'Enter the conditions for rehire.';
  if (!form.gear)
    errors['form.gear'] = 'Choose how much issued gear was returned.';
  // Every security question needs an answer; N/A counts
  const security = {
    credit_card: 'Choose what happened to the credit card, or N/A.',
    driver_pin: 'Choose whether the driver pin was cancelled, or N/A.',
    key_fob: 'Choose whether the key fob was cancelled or turned in, or N/A.',
    alarm_codes:
      'Choose whether alarm and security codes were cancelled, or N/A.',
    entry_codes: 'Choose whether entry codes were changed, or N/A.',
  };
  Object.entries(security).forEach(([field, message]) => {
    if (!form[field]) errors[`form.${field}`] = message;
  });
  if (
    (form.key_fob === 'yes' || form.key_fob === 'no') &&
    !form.key_fob_number.trim()
  )
    errors['form.key_fob_number'] = 'Enter the key fob number.';
  if (!signature) errors.signature = 'Add your signature.';
  if (!confirm) errors.confirm = 'Tick the confirmation to continue.';
  return errors;
};

const RefusalPanel = ({ hash, employeeName }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const refuse = useRecordRefusalAsSigner({
    onError: (err) => setError(getFieldErrors(err)?.note?.[0] ?? ''),
  });

  const submit = () => {
    const noteError = refusalNoteError(note);
    if (noteError) {
      setError(noteError);
      return;
    }
    setError('');
    refuse.mutate({ hash, note: note.trim() });
  };

  return (
    <div className={styles.refusal_panel}>
      <span className={styles.field_label}>
        If {employeeName} refuses to sign
      </span>
      {!open ? (
        <button
          type='button'
          className={`button outlined ${styles.small_button}`}
          onClick={() => setOpen(true)}
        >
          Record refused to sign
        </button>
      ) : (
        <>
          <label htmlFor='refusal-note' className={styles.field_label}>
            Note (required): describe in a sentence what happened and who was
            present
          </label>
          <textarea
            id='refusal-note'
            rows={3}
            className={`${styles.textarea} ${error ? styles.invalid : ''}`}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder='For example: notice explained in person on Oct 13; employee declined to sign'
            aria-describedby={error ? 'refusal-note-error' : undefined}
          />
          <FieldError id='refusal-note-error' message={error} />
          <p className={styles.hint}>
            The notice is closed as &quot;Refused to sign&quot;. The lay-off
            still goes ahead on the effective date.
          </p>
          <div className={styles.modal_actions}>
            <button
              type='button'
              className='button grey'
              onClick={() => setOpen(false)}
            >
              Back
            </button>
            <button
              type='button'
              className='button'
              onClick={submit}
              disabled={refuse.isLoading}
            >
              {refuse.isLoading ? 'Recording...' : 'Record refusal'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const StatusPanel = ({ layoff }) => {
  const employee = layoff.employee.name;
  switch (layoff.status) {
    case 1:
      return (
        <p>
          Waiting for {layoff.signers.join(' or ') || 'a superintendent'} to set
          the last day worked and sign.
        </p>
      );
    case 2:
      return (
        <>
          <div className={styles.success_icon} aria-hidden='true'>
            ✓
          </div>
          <h2 className={styles.section_title}>Signed and sent</h2>
          <p>
            Signed by <strong>{layoff.signed_by}</strong> on{' '}
            {formatDate(layoff.signed_at)}. Last day worked{' '}
            <strong>{formatDate(layoff.effective_date)}</strong>.
          </p>
          <p>
            {employee} was notified in the app and by email and can sign now.
            After they sign, the form is reviewed and sent to Payroll and HR.
          </p>
          {layoff.can.record_refusal && (
            <RefusalPanel hash={layoff.hash} employeeName={employee} />
          )}
        </>
      );
    case 9:
    case 3:
    case 4: {
      const stage =
        layoff.status === 9
          ? 'It is now waiting for review before it goes to Payroll and HR.'
          : `It was finalized and sent to Payroll and HR on ${formatDate(layoff.finalized_at)}.`;
      return layoff.refusal ? (
        <div className={styles.refused_box} role='status'>
          <strong>{employee} refused to sign.</strong> Recorded by{' '}
          {layoff.refusal.by} on {formatDate(layoff.refusal.at)}.
          <div>Note: {layoff.refusal.note}</div>
          <div>{stage}</div>
        </div>
      ) : (
        <>
          <div className={styles.success_icon} aria-hidden='true'>
            ✓
          </div>
          <h2 className={styles.section_title}>Signed by {employee}</h2>
          <p>
            {employee} signed on {formatDate(layoff.employee_signed_at)}.{' '}
            {stage}
          </p>
        </>
      );
    }
    case 5:
      return (
        <p>
          This lay-off was cancelled
          {layoff.cancel_reason ? `: ${layoff.cancel_reason}` : '.'} No
          signature is needed.
        </p>
      );
    case 6:
      return (
        <p>
          This lay-off notice was voided because it was sent in error
          {layoff.void_reason ? `: ${layoff.void_reason}` : '.'} No action is
          needed.
        </p>
      );
    default:
      return <p>This lay-off notice no longer needs your signature.</p>;
  }
};

const SignLayoff = ({ match }) => {
  const { hash } = match.params;
  const { data: layoff, isLoading, isError, error } = useSignatureDetail(hash);
  const { data: savedSignature } = useSavedSignature();

  const [effectiveDate, setEffectiveDate] = useState('');
  const [form, setForm] = useState(EMPTY_SECTIONS);
  const [signature, setSignature] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [tried, setTried] = useState(false);
  const [draft, setDraft] = useState(null);

  const canSign = !!layoff?.can?.sign;

  // Refresh the preview a second after the superintendent stops typing
  useEffect(() => {
    if (!canSign) {
      return undefined;
    }
    const timer = setTimeout(
      () =>
        setDraft({
          effective_date: effectiveDate || null,
          form,
          signature: signature || null,
        }),
      1000,
    );
    return () => clearTimeout(timer);
  }, [canSign, effectiveDate, form, signature]);

  const preview = useSignaturePreview(
    hash,
    canSign ? draft : null,
    !!layoff && (!canSign || !!draft),
  );

  const values = { effectiveDate, form, signature, confirm };
  const clientErrors =
    tried && layoff ? validate(values, layoff.min_effective_date) : {};
  const shownErrors = { ...errors, ...clientErrors };

  const sign = useSignLayoff({
    onError: (err) => {
      const fieldErrors = getFieldErrors(err);
      if (fieldErrors) {
        setErrors(firstErrors(fieldErrors));
      }
    },
  });

  const setField = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [`form.${field}`]: undefined }));
  };

  const submit = () => {
    setTried(true);
    if (Object.keys(validate(values, layoff.min_effective_date)).length) {
      return;
    }
    sign.mutate({
      hash,
      effective_date: effectiveDate,
      form,
      signature,
      confirm,
    });
  };

  if (isLoading) {
    return (
      <div className={styles.page_message}>
        <img
          alt='Loading'
          src={require('../../assets/images/loader.gif').default}
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.page_message}>
        {error?.response?.status === 403
          ? "You can't open this lay-off notice."
          : 'This lay-off notice could not be loaded.'}{' '}
        <Link to='/layoff/sign'>Back to lay-off signatures</Link>
      </div>
    );
  }

  const status = getLayoffStatus(layoff.status);

  return (
    <div className='activity-log'>
      <div className='header'>
        <div className='report_tbl_head sold_rpt_mbl_flx'>
          <h4 className='graph_header'>
            Lay-off notice: {layoff.employee.name}
          </h4>
          <div className='buttons'>
            <Link to='/layoff/sign' className='button outlined'>
              Back to lay-off signatures
            </Link>
          </div>
        </div>
      </div>
      <div className='white'>
        <div className={`inner_container col-lg-12 ${styles.send_layout}`}>
          <section
            aria-label='Document preview'
            className={`${styles.picker} ${styles.document_panel}`}
          >
            <div className={styles.document_head}>
              <span>
                Employee Discharge Form · {layoff.crew} · sent{' '}
                {formatDate(layoff.sent_at)} by {layoff.sent_by}
              </span>
              <DocStatusTag
                status={layoff.status_label}
                bgColor={status.bg}
                fontColor={status.fg}
                className={styles.status_tag}
              />
            </div>
            {canSign && (
              <p className={styles.hint}>
                Live preview: updates a moment after you change your answers.
              </p>
            )}
            {preview.isError && (
              <p>The preview could not be loaded. Please try again.</p>
            )}
            {preview.data ? (
              <PdfDocument file={preview.data} />
            ) : (
              <div className={styles.empty}>Loading preview...</div>
            )}
          </section>

          <aside
            aria-label='Your part'
            className={`${styles.summary} ${styles.sign_panel}`}
          >
            {!canSign ? (
              <StatusPanel layoff={layoff} />
            ) : (
              <>
                <h2 className={styles.summary_title}>Your part</h2>

                <div>
                  <label
                    htmlFor='effective-date'
                    className={styles.field_label}
                  >
                    1. Lay-off effective date (last day worked)
                    <RequiredMark />
                  </label>
                  <DateInput
                    id='effective-date'
                    min={layoff.min_effective_date}
                    invalid={!!shownErrors.effective_date}
                    // The error (when shown) and the hint are both read out
                    describedBy={
                      shownErrors.effective_date
                        ? 'effective-date-error effective-date-hint'
                        : 'effective-date-hint'
                    }
                    value={effectiveDate}
                    onChange={(value) => {
                      setEffectiveDate(value);
                      setErrors((prev) => ({
                        ...prev,
                        effective_date: undefined,
                      }));
                    }}
                  />
                  <FieldError
                    id='effective-date-error'
                    message={shownErrors.effective_date}
                  />
                  <span id='effective-date-hint' className={styles.hint}>
                    Printed on the form as the lay-off effective date and last
                    day worked.
                  </span>
                </div>

                <SupervisorSections
                  form={form}
                  setField={setField}
                  errors={shownErrors}
                  start={2}
                />

                <SignatureField
                  required
                  label='7. Your signature'
                  value={signature}
                  onChange={(value) => {
                    setSignature(value);
                    setErrors((prev) => ({ ...prev, signature: undefined }));
                  }}
                  error={shownErrors.signature}
                  savedSignature={savedSignature}
                />

                <div className={styles.confirm_row}>
                  <input
                    id='confirm'
                    type='checkbox'
                    checked={confirm}
                    onChange={() => {
                      setConfirm(!confirm);
                      setErrors((prev) => ({ ...prev, confirm: undefined }));
                    }}
                  />
                  <label htmlFor='confirm'>
                    I have reviewed this form and confirm the last day worked.
                    <RequiredMark />
                  </label>
                </div>
                <FieldError message={shownErrors.confirm} />

                {Object.values(shownErrors).some(Boolean) && (
                  <div className={styles.error_text} role='alert'>
                    Some answers need attention. See the messages above.
                  </div>
                )}

                <button
                  type='button'
                  className='button'
                  onClick={submit}
                  disabled={sign.isLoading}
                >
                  {sign.isLoading ? 'Signing...' : 'Sign and send to employee'}
                </button>
                <p className={styles.hint}>
                  {layoff.signers.length > 1
                    ? `Any of ${layoff.signers.join(', ')} can sign; the first to sign completes this step. `
                    : ''}
                  Once you sign, {layoff.employee.name} can only use this notice
                  in Firestorm until they sign it.
                </p>
              </>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default SignLayoff;
